import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { authHubAuth } from './config/firebase.js';
import { buildApiUrl, isLocalEnvironment } from './utils/apiBase.js';
import {
  signOutEverywhere,
  restoreFirebaseAuthFromSession,
  applySsoSessionData,
  applyVerifySessionData
} from './utils/authSession.js';

import LoadingSpinner from './components/common/LoadingSpinner.jsx';
import PublicLayout from './components/hub/layout/PublicLayout.jsx';
import HubLayout from './components/hub/layout/HubLayout.jsx';
import LandingPage from './components/hub/landing/LandingPage.jsx';
import ProductsPage from './components/hub/products/ProductsPage.jsx';
import PricingPage from './components/hub/pricing/PricingPage.jsx';
import HubLoginPage from './components/hub/auth/HubLoginPage.jsx';
import SignupPage from './components/hub/auth/SignupPage.jsx';
import ResetPasswordPage from './components/hub/auth/ResetPasswordPage.jsx';
import ResetPasswordConfirmPage from './components/hub/auth/ResetPasswordConfirmPage.jsx';
import VerifyEmailPage from './components/hub/auth/VerifyEmailPage.jsx';
import VerifyEmailPendingPage from './components/hub/auth/VerifyEmailPendingPage.jsx';
import Dashboard from './components/hub/dashboard/Dashboard.jsx';
import ModulesPage from './components/hub/modules/ModulesPage.jsx';
import LicensesPage from './components/hub/licenses/LicensesPage.jsx';
import BillingPage from './components/hub/billing/BillingPage.jsx';
import AccountSettings from './components/hub/settings/AccountSettings.jsx';
import ClinicSettings from './components/hub/settings/ClinicSettings.jsx';
import UsersManagement from './components/hub/settings/UsersManagement.jsx';
import InvitationCode from './components/hub/settings/InvitationCode.jsx';
import AppIntegrations from './components/hub/settings/AppIntegrations.jsx';
import HubContactPage from './components/hub/support/HubContactPage.jsx';
import HubLegalIndexPage from './components/hub/support/HubLegalIndexPage.jsx';
import HubLegalDocumentPage from './components/hub/support/HubLegalDocumentPage.jsx';
import PrivacyPolicyPage from './components/hub/legal/PrivacyPolicyPage.jsx';
import TermsOfServicePage from './components/hub/legal/TermsOfServicePage.jsx';
import CommercePage from './components/hub/legal/CommercePage.jsx';
import CrossBorderDisclosurePage from './components/hub/legal/CrossBorderDisclosurePage.jsx';
import ContactPage from './components/hub/contact/ContactPage.jsx';
import { BetaProvider } from './contexts/BetaContext.jsx';
import { ToastProvider } from './contexts/ToastContext.jsx';

import './styles/hub.css';

const ProtectedRoute = ({ children, user, loading }) => {
  const location = useLocation();

  if (loading) {
    return (
      <div className="app-loading">
        <LoadingSpinner size="large" />
        <p>読み込み中...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const GuestRoute = ({ children, user, loading }) => {
  if (loading) {
    return (
      <div className="app-loading">
        <LoadingSpinner size="large" />
        <p>読み込み中...</p>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const RouteBlur = () => {
  const location = useLocation();

  useEffect(() => {
    document.body.classList.add('route-blur');
    const timer = setTimeout(() => {
      document.body.classList.remove('route-blur');
    }, 300);
    return () => {
      clearTimeout(timer);
      document.body.classList.remove('route-blur');
    };
  }, [location.pathname]);

  return null;
};

function AppContent() {
  const [user, setUser] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ssoChecked, setSsoChecked] = useState(false);
  const restoredUidRef = useRef(null);

  const restoreFirebaseAuthSilently = useCallback(async (expectedUid) => {
    if (expectedUid) {
      restoredUidRef.current = expectedUid;
    }
    const ok = await restoreFirebaseAuthFromSession();
    if (!ok) {
      restoredUidRef.current = null;
    }
    return ok;
  }, []);

  const fetchSsoCheck = useCallback(async () => {
    const response = await fetch(buildApiUrl('/api/auth/sso-check'), {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
    });
    if (!response.ok) return null;
    return response.json();
  }, []);

  useEffect(() => {
    const checkSSOSession = async () => {
      if (isLocalEnvironment()) {
        setSsoChecked(true);
        return;
      }

      try {
        const sessionData = await fetchSsoCheck();
        if (sessionData?.authenticated) {
          applySsoSessionData(sessionData, setUser, setClinic, setLicenses);
          setLoading(false);
          await restoreFirebaseAuthSilently(sessionData.user.uid);
        }
      } catch (error) {
        console.log('[SSO] Session Cookie チェックエラー:', error.message);
      } finally {
        setSsoChecked(true);
      }
    };

    checkSSOSession();
  }, [fetchSsoCheck, restoreFirebaseAuthSilently]);

  useEffect(() => {
    if (!ssoChecked) return;

    const isLocal = isLocalEnvironment();

    const unsubscribe = onAuthStateChanged(authHubAuth, async (authHubUser) => {
      if (!authHubUser) {
        if (isLocal) {
          setUser(null);
          setClinic(null);
          setLicenses([]);
          setLoading(false);
          return;
        }

        try {
          const sessionData = await fetchSsoCheck();
          if (sessionData?.authenticated) {
            applySsoSessionData(sessionData, setUser, setClinic, setLicenses);
            setLoading(false);
            await restoreFirebaseAuthSilently(sessionData.user.uid);
            return;
          }
        } catch {
          // ignore
        }

        setUser(null);
        setClinic(null);
        setLicenses([]);
        setLoading(false);
        return;
      }

      if (authHubUser.uid === restoredUidRef.current) {
        restoredUidRef.current = null;
        return;
      }

      setLoading(true);

      try {
        const idToken = await authHubUser.getIdToken();
        const response = await fetch(buildApiUrl('/api/auth/verify'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          credentials: 'include'
        });

        const sessionData = await response.json().catch(() => ({}));
        if (!response.ok || !sessionData.success) {
          throw new Error(sessionData.error || sessionData.message || '認証に失敗しました');
        }

        applyVerifySessionData(sessionData, setUser, setClinic, setLicenses);
      } catch (error) {
        console.error('認証処理エラー:', error);
        setUser(null);
        setClinic(null);
        setLicenses([]);
        await signOutEverywhere();
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [ssoChecked, fetchSsoCheck, restoreFirebaseAuthSilently]);

  const handleLogout = useCallback(() => {
    setUser(null);
    setClinic(null);
    setLicenses([]);
    setLoading(false);
  }, []);

  const refreshSessionFromApi = useCallback(async () => {
    try {
      const sessionData = await fetchSsoCheck();
      if (!sessionData?.authenticated || !sessionData.user) return;
      applySsoSessionData(sessionData, setUser, setClinic, setLicenses);
      if (!authHubAuth.currentUser) {
        await restoreFirebaseAuthSilently(sessionData.user.uid);
      }
    } catch (err) {
      console.log('[Session] 再取得エラー:', err?.message);
    }
  }, [fetchSsoCheck, restoreFirebaseAuthSilently]);

  return (
    <BetaProvider user={user}>
      <RouteBlur />
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/commerce" element={<CommercePage />} />
          <Route path="/crossborder" element={<CrossBorderDisclosurePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route
            path="/login"
            element={
              <GuestRoute user={user} loading={loading}>
                <HubLoginPage />
              </GuestRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <GuestRoute user={user} loading={loading}>
                <SignupPage />
              </GuestRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <GuestRoute user={user} loading={loading}>
                <ResetPasswordPage />
              </GuestRoute>
            }
          />
          <Route
            path="/reset-password/confirm"
            element={
              <GuestRoute user={user} loading={loading}>
                <ResetPasswordConfirmPage />
              </GuestRoute>
            }
          />
          {/* 確認リンクはログイン中（メール変更）でも到達できるよう GuestRoute にしない */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/verify-email/pending" element={<VerifyEmailPendingPage />} />
        </Route>

        <Route
          element={
            <ProtectedRoute user={user} loading={loading}>
              <HubLayout
                user={user}
                clinic={clinic}
                licenses={licenses}
                setLicenses={setLicenses}
                onRefreshSession={refreshSessionFromApi}
                onLogout={handleLogout}
              />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/modules" element={<ModulesPage />} />
          <Route path="/licenses" element={<LicensesPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/settings/account" element={<AccountSettings />} />
          <Route path="/settings/clinic" element={<ClinicSettings />} />
          <Route path="/settings/users" element={<UsersManagement />} />
          <Route path="/settings/integrations" element={<AppIntegrations />} />
          <Route path="/settings/invitation" element={<InvitationCode />} />
          <Route path="/support/contact" element={<HubContactPage />} />
          <Route path="/support/legal" element={<HubLegalIndexPage />} />
          <Route path="/support/legal/terms" element={<HubLegalDocumentPage docType="terms" />} />
          <Route path="/support/legal/privacy" element={<HubLegalDocumentPage docType="privacy" />} />
          <Route path="/support/legal/commerce" element={<HubLegalDocumentPage docType="commerce" />} />
          <Route path="/settings" element={<Navigate to="/settings/account" replace />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BetaProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
